import React, { useState, useEffect, useRef } from "react";
import { Delete, DeletePublisher, DeleteTag } from "../../services/delete";
import api from "../../services/apiClient";
import Button from "../../components/inputs/button";
import BulkProgressIndicator from "./BulkProgressIndicator";
import ModalSurface from "./ModalSurface";
import useBatchOperation from "../../hooks/useBatchOperation";
import Toast from "../../components/toast";
import PropTypes from "prop-types";
import "./index.scss";
import Loader from "../misc/loader";

function DeleteConfirmation({
	cohort,
	user,
	publisher,
	tag,
	getTableData,
	tagCohorts,
	getCohorts,
	onUpdate,
}) {
	const uniqueIds = cohort ? cohort.map((cohort) => cohort.value) : "";
	const [activeSilo, setActiveSilo] = useState(localStorage.getItem("activeSilo"));
	const [toast, setToast] = useState(null);
	const [loading, setLoading] = useState(false);

	const timerRef = useRef(null);
	const { execute, cancel, state: batchState } = useBatchOperation({ batchSize: 5 });

	const handleDelete = async () => {
		if (user) {
			Delete(user.uid).then((r) => {
				r.json().then((b) =>{
					setToast({
						message: b.message,
						status: "Success",
						title: "Success",
					});
				});
			}).catch((error) => {
				const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
				const message = errors ? errors.map((e) => e[1]).join("\n") : (error.data?.message || error.message || "Delete failed");
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? error.data.message : "Error",
				});
			});
		} else if (cohort) {
			setLoading(true);
			const tasks = uniqueIds.map((id) => ({
				id,
				run: () => api.delete(`/cohorts/${id}`).then((r) => r.json()),
			}));

			try {
				const result = await execute(tasks);

				if (result.cancelled) {
					setToast({
						message: `Operation cancelled. ${result.succeeded} of ${uniqueIds.length} audience(s) deleted.`,
						status: "Invalid",
						title: "Cancelled",
					});
				} else if (result.failed === 0) {
					setToast({
						message: "Audience(s) Deleted",
						status: "Success",
						title: "Success",
					});
				} else if (result.succeeded === 0) {
					setToast({
						message: "Failed to delete audience(s)",
						status: "Invalid",
						title: "Error",
					});
				} else {
					setToast({
						message: `${result.succeeded} of ${uniqueIds.length} audience(s) deleted. ${result.failed} failed.`,
						status: "Invalid",
						title: "Partial Success",
					});
				}
			} catch (error) {
				setToast({
					message: error.message || "An unexpected error occurred",
					status: "Invalid",
					title: "Error",
				});
			}
			setLoading(false);
		} else if (publisher) {
			DeletePublisher(publisher.pid).then((r) => {
				r.json().then((b) =>{
					setToast({
						message: b.message,
						status: "Success",
						title: "Success",
					});
				});
			}).catch((error) => {
				const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
				const message = errors ? errors.map((e) => e[1]).join("\n") : (error.data?.message || error.message || "Delete failed");
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? error.data.message : "Error",
				});
			});
		} else if (tag) {
			DeleteTag(tag.tagId).then((r) => {
				r.json().then((b) =>{
					setToast({
						message: b.message,
						status: "Success",
						title: "Success",
					});
				});
			}).catch((error) => {
				const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
				const message = errors ? errors.map((e) => e[1]).join("\n") : (error.data?.message || error.message || "Delete failed");
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? error.data.message : "Error",
				});
			});
		}
	};

	useEffect(() => {
		if (toast && toast.status === "Success") {
			timerRef.current = setTimeout(() => {
				toastClose();
			}, 2000);
		}
	}, [toast]);

	const postDelete = () => {
		if (user || publisher || tag) {
			getTableData();
		} else if (cohort) {
			getCohorts(activeSilo);
		}
		onUpdate();
		if (cohort) {
			tagCohorts("");
		}
	};

	const toastClose = () => {
		setToast(null);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		postDelete();
	};

	if (toast) {
		return (
			<Toast
				status={toast.status}
				message={toast.message}
				title={toast.title}
				callback={() => toastClose()}
				autoHide={false}
			/>
		);
	}

	let title = "Confirm Delete";
	let entityName = null;
	if (user) {
		title = "Permanently Delete User";
		entityName = (
			<span>
				{`${user.firstname || ""} ${user.lastname || ""}`.trim() || <Loader size={"sm"} />}
			</span>
		);
	} else if (cohort) {
		title = `Permanently Delete ${uniqueIds.length > 1 ? "Audiences" : "Audience"}`;
		entityName = cohort.map((cohort) => cohort.label).join("\n");
	} else if (publisher) {
		title = "Permanently Delete Publisher";
		entityName = publisher.publisher_name;
	} else if (tag) {
		title = "Permanently Delete ArcTag";
		entityName = tag.name;
	}

	const footer = (
		<>
			<button
				type="button"
				className="cancel-link"
				onClick={() => onUpdate()}
				disabled={loading}
			>
				Cancel
			</button>
			<Button
				loaded={loading}
				onClick={handleDelete}
				text="Delete"
				style="dark"
				type="button"
				disabled={loading}
			/>
		</>
	);

	return (
		<ModalSurface title={title} onClose={onUpdate} footer={footer} closeDisabled={loading}>
			<div className="modal-warning">
				<svg
					className="modal-warning__icon"
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="M8 1.5L15 14.5H1L8 1.5Z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					<path
						d="M8 6V10"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
					<circle cx="8" cy="12" r="0.85" fill="currentColor" />
				</svg>
				<span>This action cannot be undone.</span>
			</div>
			<div className="modal-audience-list p3">{entityName}</div>
			{cohort && <BulkProgressIndicator state={batchState} onCancel={cancel} />}
		</ModalSurface>
	);
}

DeleteConfirmation.propTypes = {
	user: PropTypes.object.isRequired,
	getTableData: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	cohort: PropTypes.any,
	publisher: PropTypes.object,
	tag: PropTypes.object,
	tagCohorts: PropTypes.func,
	getCohorts: PropTypes.func,
};

export { DeleteConfirmation };
